import React from 'react';
import { useLocation, useNavigate, matchPath } from 'react-router-dom';
import {
  Box,
  BoxProps,
  Stack,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Icon,
  ListItemText,
} from '@mui/material';

import { getAuthState } from '../../../../store/AuthSlice';
import { getAccessLimitState } from '../../../../store/AccessLimitSlice';
import { USER_PERMISSION } from '../../../../store/V2/RolesSlice';
import {
  INavigationRoutes,
  userNavigationRoutes,
} from '../../../../navigation';
import { useAppSelector } from '../../../../hooks';
import { SafeAreaGaps } from '../../../../helper/Mobile';
import { checkUserPermission } from '../../../../helper';
import ThemeSwitcher from '../../../../components/Button/ThemeSwitcher';

export const drawerWidth = 240;

const containerStyles: BoxProps['style'] = {
  paddingTop: `calc(${SafeAreaGaps.top} + 50px)`,
  paddingBottom: `calc(${SafeAreaGaps.bottom} + 15px)`,
  paddingLeft: `calc(${SafeAreaGaps.top} + 15px)`,
  paddingRight: `calc(${SafeAreaGaps.right} + 15px)`,
  height: '100%',
};

const DrawerContents: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Store
  const authState = useAppSelector(getAuthState);
  const accessLimitState = useAppSelector(getAccessLimitState);

  // Permissions
  const adminStudentPermission = checkUserPermission(authState.user, {
    adminStudent: ['view'],
  });

  const quizHistoryPermission = checkUserPermission(authState.user, {
    quizHistory: ['view'],
  });

  const coursePermission = checkUserPermission(authState.user, {
    course: USER_PERMISSION.COURSE,
  });

  const filterOutUnAuthorizedRoutes = (
    userNavigationRoute: INavigationRoutes
  ) => {
    if (userNavigationRoute.key === 'students' && !adminStudentPermission) {
      return false;
    }

    if (userNavigationRoute.key === 'quiz-history' && !quizHistoryPermission) {
      return false;
    }

    if (
      userNavigationRoute.key === 'subscriptions' &&
      !accessLimitState.lpAccess
    ) {
      return false;
    }

    if (userNavigationRoute.key === 'courses' && !coursePermission) {
      return false;
    }

    return true;
  };

  return (
    <Box component="div" role="presentation" style={containerStyles}>
      <Stack justifyContent="space-between" sx={{ height: '100%' }}>
        <div>
          <Stack direction="row" justifyContent="center">
            <a
              href={`${process.env.PUBLIC_URL}/`}
              style={{ marginRight: '10px' }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/logos/tp-logo-text-green-new.png`}
                alt="tp-logo"
                style={{ width: '120px' }}
              />
            </a>
          </Stack>
          <List sx={{ mt: '30px' }}>
            {userNavigationRoutes
              .filter(filterOutUnAuthorizedRoutes)
              .map((userNavigationRoute, index) => {
                let isMatch = false;
                const isSelected =
                  location.pathname === userNavigationRoute.pathname;

                if (
                  userNavigationRoute.matches &&
                  userNavigationRoute.matches.length > 0
                ) {
                  userNavigationRoute.matches.forEach((match) => {
                    if (
                      matchPath(match, location.pathname) &&
                      isMatch === false
                    ) {
                      isMatch = true;
                    }
                  });
                }

                const handleNavigationChange = () => {
                  if (!isSelected) {
                    setTimeout(() => {
                      navigate(userNavigationRoute.pathname);
                    }, 250);
                  }
                };

                const isActive = isMatch || isSelected;

                return (
                  <ListItem key={index} disablePadding sx={{ mb: '5px' }}>
                    <ListItemButton
                      selected={isActive}
                      sx={{ borderRadius: '7px' }}
                      onClick={handleNavigationChange}
                    >
                      <ListItemIcon>
                        <Icon
                          color={isActive ? 'primary' : 'inherit'}
                          component={userNavigationRoute.icon}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={userNavigationRoute.name}
                        primaryTypographyProps={{
                          color: isActive ? 'primary' : 'secondary',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
          </List>
        </div>
        <div>
          <Stack alignItems="center">
            <ThemeSwitcher />
          </Stack>
        </div>
      </Stack>
    </Box>
  );
};

export default DrawerContents;
